import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    forwardRef,
    Injector,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, NgControl, ValidationErrors } from '@angular/forms';
import { Overlay } from 'primeng/overlay';
import { Nullable } from 'primeng/ts-helpers';

/**
 * MultiSelect is used to select multiple items from a collection.
 * @group Components
 */
@Component({
    selector: 'nx-filter-numberselect',
    template: `
    <div class="wrapper nx-filter-numberselect flex flex-row"
        [ngClass]="{'inputwrapper-filled': value !== undefined}"
        (click)=clicked()>
        <div
            *ngIf="value === undefined"
            class="nx-filter-numberselect-trigger">
            <div class="nx-filter-numberselect-trigger-icon ng-star-inserted">+</div>
        </div>
        <div
            *ngIf="value !== undefined"
            (click)="clear(); $event.stopPropagation()"
            class="nx-filter-numberselect-clear">
            <div class="nx-filter-numberselect-clear-icon ng-star-inserted">+</div>
        </div>
        <div class="placeholder">
            {{placeholder}}<ng-container *ngIf="value">:&nbsp;</ng-container>
        </div>
        <div class="value" *ngIf="value">
            {{value}}
        </div>
        <div class="suffix" *ngIf="suffix && value">
            &nbsp;{{suffix}}
        </div>
        <p-overlay
            #overlay
            [styleClass]="'nx-filter-numberselect'"
            [(visible)]="overlayVisible"
            [target]="'@parent'"
            [appendTo]="'body'">
            <ng-template pTemplate="content">
                <div class="content form" [formGroup]="numberFormGroup">
                    <div class="text-base font-medium leading-3r">Aufträge mit Fälligkeit in der Zukunft anzeigen</div>
                    <div class="text-base text-[#848488] font-light leading-3 py-1 flex flex-row">
                        <div class="pt-[9px] mr-2">Tage (0-30):</div>
                        <div class="flex-grow">
                            <input formControlName="number" class="p-inputtext" type="number" [min]="min" [max]="max" [step]="step">
                        </div>
                    </div>
                    <nx-default-button
                        [width]="'full'"
                        class="w-full"
                        [style]="'dark'"
                        [disabled]="numberFormGroup.invalid"
                        (clicked)="clicked()"
                        [label]="'Anwenden'">
                    </nx-default-button>
                </div>
            </ng-template>
        </p-overlay>
    </div>
    `,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NxFilterNumberSelectComponent),
        multi: true
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./nx-filter-numberselect.css']
})
export class NxFilterNumberSelectComponent implements ControlValueAccessor, OnInit {
    
    value: number;
    overlayVisible: Nullable<boolean>;
    @ViewChild('overlay') overlayViewChild: Nullable<Overlay>;
    @Input() placeholder: string;
    @Input() suffix: string;
    @Input() initialValue: number;
    @Input() min = 0;
    @Input() max = 100;
    @Input() step = 1;
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- bewusster PrimeNG-API-Mirror, intern (#698)
    @Output() onChange = new EventEmitter<number>();
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix -- bewusster PrimeNG-API-Mirror, intern (#698)
    @Output() onClear = new EventEmitter<number>();

    numberFormGroup: FormGroup;
    // formValueSubscription: Subscription;
    ngControl: NgControl;

    private _onChange: (value: number) => void;
    private onTouched: () => void;

    constructor(private _injector: Injector) {

    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
        this.numberFormGroup = new FormGroup({});
        this.numberFormGroup.addControl('number', new FormControl(this.initialValue));
        if(this.min && this.max) {
            this.numberFormGroup.get('number')?.setValidators([this.validateNumberRange.bind(this)]);
        }
        // this.formValueSubscription = this.numberFormGroup.valueChanges.subscribe(() => {
        //     this.value = this.numberFormGroup.get('number')?.value;
        // });
    }

    writeValue(obj: any): void {
        this.numberFormGroup.get('number')?.setValue(obj);
        this.value = obj;
        if (this._onChange) {
            this._onChange(this.value);
        }
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        if(isDisabled) {
            this.numberFormGroup.disable();
        } else {
            this.numberFormGroup.enable();
        }
    }

    clicked() {
        if (this.overlayVisible) {
            this.value = this.numberFormGroup.get('number')?.value;
            this._onChange(this.value);
            this.onChange.emit(this.value); 
            this.overlayViewChild.hide();
        } else {
            this.overlayViewChild.show();
        }
    }

    validateNumberRange(control: AbstractControl): ValidationErrors | null {
        const value = control.value;
        if (value < this.min || value > this.max) {
            return { rangeError: true };
        }
        return null;
    }

    clear() {
        this.numberFormGroup.get('number')?.setValue(undefined);
        this.value = undefined;
        this._onChange(undefined);
        this.onClear.emit()

    }

}